import { inject } from '@angular/core';
import type { ResolveFn } from '@angular/router';

import { DefaultSeoService } from '@rpgc-ai/services/seo/default-seo.service';
import { SeoService } from '@rpgc-ai/services/seo/seo.service';
import { SeoDataService } from '@rpgc-ai/services/seo/seo-data.service';
import { LanguageService } from '@rpgc-ai/services/translate/language.service';

import type { ELanguage } from '@rpgc-ai/shared-interfaces/interfaces/language.interface';
import { buildCharacterPageTitle } from '@rpgc-ai/shared-interfaces/utils/character-page-title.util';
import { getLocalizedPublicName } from '@rpgc-ai/shared-interfaces/utils/localized-character-name.util';
import { getLocalizedDescription } from '@rpgc-ai/shared-interfaces/utils/localized-description.util';

import { environment } from '@rpgc-ai/environments';

import { catchError, forkJoin, map, tap } from 'rxjs';

/**
 * Оба слага вместе адресуют карточку в канонической форме `view-chatbot/<authorSlug>/<characterSlug>`;
 * короткая форма маршрута их не содержит, поэтому резолвер публикует пару в `data.seo`.
 */
export interface IChatbotCanonicalSlugs {
  readonly characterSlug: string;
  readonly authorSlug: string;
}

export const chatbotSeoResolver: ResolveFn<IChatbotCanonicalSlugs | undefined> = (route) => {
  const seoDataService = inject(SeoDataService);
  const seoService = inject(SeoService);
  const defaultSeoService = inject(DefaultSeoService);
  const languageService = inject(LanguageService);

  const botId = route.params['botId'];
  if (!botId) {
    return;
  }

  const routeLanguage = languageService.getLanguageForRoute(route);
  const lang = routeLanguage.toUpperCase() as ELanguage;

  return forkJoin([
    seoDataService.getCharacter(botId, route.params['creatorSlug']),
    defaultSeoService.selectTexts(routeLanguage),
  ]).pipe(
    tap(([character, defaultSeo]) => {
      const title = buildCharacterPageTitle(getLocalizedPublicName(character, lang), environment.titleSuffix);
      const description = getLocalizedDescription(character.descriptions, lang) || defaultSeo.description;

      seoService.setTitle(title);
      seoService.setTag('description', description, 'name');
      seoService.setTag('og:title', title, 'property');
      seoService.setTag('og:description', description, 'property');
    }),
    map(([{ slug, userSlug }]) => ({ characterSlug: slug, authorSlug: userSlug })),
    catchError((error: unknown) => {
      // eslint-disable-next-line no-console
      console.error('chatbotSeoResolver: failed to load chatbot SEO data', error);
      return defaultSeoService.apply(routeLanguage).pipe(map(() => void 0));
    }),
  );
};
